import * as React from 'react'
import type { Moment } from 'moment'

import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import { ActiveMenuItem } from '@/app/constants/menuItem'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  getReportNumberByType,
  getReportNameByType,
  getReportTypeByNumber,
} from '@/app/services/printout/reportTypeResolver'
import { usePrintPdf } from './usePrintPdf'
import { resolveReportParams } from '../utils/reportDefaultParams'
import { useBatchPrintoutStore } from '../stores/batchPrintoutStore'
import { useGeneratedReportsStore } from '../stores/generatedReportsStore'
import { useReportParamsStore } from '../stores/reportParamsStore'
import { usePrintJobGuard } from './usePrintJobGuard'

/**
 * Raporty budowane jako jedna lista dla wszystkich kierowcow
 * (IsSingleListContent po stronie serwera, bez PrepareData na kierowce).
 * Wymuszenie osobnych plikow daje dla nich pusty dokument i blad "brak danych",
 * wiec zostaja przy wydruku zbiorczym - kosztem folderu w paczce ZIP.
 */
const SINGLE_DOCUMENT_REPORTS: ReadonlySet<ReportType> = new Set([
  ReportType.VIRTUAL_DISPLACEMENT,
  ReportType.GROUPED_TRIP_BASICS,
])

export interface GenerateReportsParams {
  companyId: string
  reports: ReportType[]
  activeItem: ActiveMenuItem
  checkedDrivers: string[]
  checkedVehicles: string[]
  dateFrom: Moment
  dateTo: Moment
  outputType: OutputType
  /**
   * Nazwy zaznaczonych kierowcow/pojazdow. Raporty z opcja "osobne pliki" trafiaja
   * w paczce ZIP do folderu kierowcy.
   */
  subjectNames?: string[]
  /** Jedna paczka, po wygenerowaniu mail na adresy z konfiguracji e-mail. */
  sendByEmail?: boolean
}

// Opcje z innej firmy sa nieaktualne - wtedy domyslne.
const getReportParams = (report: ReportType, companyId: string) => {
  const { ownerCompanyId, paramsByReport } = useReportParamsStore.getState()
  return resolveReportParams(
    report,
    ownerCompanyId === companyId ? paramsByReport[report] : undefined,
  )
}

/**
 * Generowanie wielu raportow naraz - kazdy raport idzie osobnym zadaniem,
 * z opcjami ustawionymi dla niego na liscie.
 */
export const useBatchPrintout = (
  onTaskCreated?: (taskId: string, reportType: ReportType) => void,
) => {
  const { t } = useLocalize()
  const { printPdf } = usePrintPdf()
  const { isGenerating, runPrintJob } = usePrintJobGuard()
  const markGenerated = useGeneratedReportsStore((s) => s.markGenerated)
  const [sending, setSending] = React.useState(false)

  // Paczka trwa az do zebrania wszystkich dokumentow - druga paczka
  // wyczyscilaby stan pierwszej, wiec przycisk zostaje zablokowany.
  const batchInProgress = useBatchPrintoutStore(
    (s) => s.docs.length > 0 && s.docs.some((doc) => doc.status === 'pending'),
  )

  const generateBatch = React.useCallback(
    async ({
      companyId,
      reports,
      activeItem,
      checkedDrivers,
      checkedVehicles,
      dateFrom,
      dateTo,
      outputType,
      subjectNames = [],
      sendByEmail = false,
    }: GenerateReportsParams) => {
      if (reports.length === 0) return

      setSending(true)

    

      const failed: ReportType[] = []
      const { startBatch, trackTask, sealBatch } = useBatchPrintoutStore.getState()

      // Osobne pliki tylko gdy zaznaczono to w opcjach raportu.
      const getBatchData = (report: ReportType) => {
        const params = getReportParams(report, companyId)
        if (SINGLE_DOCUMENT_REPORTS.has(report)) {
          return { ...params, separateFiles: false }
        }
        return params
      }

      startBatch(subjectNames)

      if (sendByEmail) {
        const reportsPayload = reports.map((report) => ({
          reportType: getReportNumberByType(report),
          data: getBatchData(report),
        }))

        try {
          const { taskId } = await printPdf({
            companyId,
            reportType: reportsPayload[0].reportType,
            drivers:
              activeItem === ActiveMenuItem.PRINTOUT_DRIVER
                ? checkedDrivers
                : null,
            vehicles:
              activeItem === ActiveMenuItem.PRINTOUT_VEHICLE
                ? checkedVehicles
                : null,
            startTime: dateFrom,
            endTime: dateTo,
            outputType,
            data: reportsPayload[0].data,
            sendByEmail: true,
            reports: reportsPayload,
          })

          trackTask(taskId, reports[0])
          onTaskCreated?.(taskId, reports[0])
        } catch (err) {
          failed.push(...reports)
          console.error('Exception in printPdf for emailed printouts:', err)
        }
      }

      for (const report of sendByEmail ? [] : reports) {
        const reportNumber = getReportNumberByType(report)

        try {
          const { taskId } = await printPdf({
            companyId,
            reportType: reportNumber,
            drivers:
              activeItem === ActiveMenuItem.PRINTOUT_DRIVER
                ? checkedDrivers
                : null,
            vehicles:
              activeItem === ActiveMenuItem.PRINTOUT_VEHICLE
                ? checkedVehicles
                : null,
            startTime: dateFrom,
            endTime: dateTo,
            outputType,
            data: getBatchData(report),
          })

          // Zadania paczki zbieramy w podsumowaniu zamiast otwierac podglad
          // kazdego dokumentu z osobna.
          trackTask(taskId, report)
          onTaskCreated?.(taskId, getReportTypeByNumber(reportNumber))
        } catch (err) {
          failed.push(report)
          console.error(`Exception in printPdf for report ${report}:`, err)
        }
      }

      sealBatch()

      const succeeded = reports.length - failed.length

      markGenerated(reports.filter((report) => !failed.includes(report)))

    

      if (failed.length > 0) {
        toast.error({
          title: t('printout.generation-error'),
          text: failed
            .map((report) => t(getReportNameByType(report)))
            .join(', '),
          autoCloseMs: 5000,
        })
      }

      setSending(false)

      return { succeeded, failed }
    },
    [printPdf, onTaskCreated, markGenerated, t],
  )

  const generateReports = React.useCallback(
    (params: GenerateReportsParams) => runPrintJob(() => generateBatch(params)),
    [generateBatch, runPrintJob],
  )

  return {
    generateReports,
    pending: sending || batchInProgress || isGenerating,
  }
}
